/* Vue admin — contenus de toute la plateforme (bloc 10c). Lecture seule : */
/* liste + filtres + détail des documents créés par tous les profs de toutes */
/* les écoles. Aucune action d'écriture ici (pas de création/édition/archivage/publication). */

import { fmtListDate, fmtListDateShort, LIST_ROW_META_CLASS, LIST_ROW_TITLE_CLASS } from '../lib/list-row-classes';
import { displayTitle } from '../lib/display-title';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ArrowLeft, ArrowRight, ArrowClockwise, BookOpen, FileText,
  Files, ListChecks, MagnifyingGlass, PencilSimple, WarningCircle,
} from '@phosphor-icons/react';
import { fetchAdminSchools, type SchoolRow } from '../lib/admin-users';
import {
  listAdminContentDocuments,
  listAdminContentTeachers,
  type AdminContentDocument,
  type AdminContentDocumentKind,
  type AdminContentTeacherOption,
} from '../lib/admin-content';

const HEADER_ICON = <Files size={26} />;

const PAGE_SIZE = 20;

const KIND_LABEL: Record<AdminContentDocumentKind, string> = {
  general: 'Document',
  course: 'Cours',
  exercice_sheet: 'Exercices',
  exam: 'Évaluation',
};

const KIND_ICON: Record<AdminContentDocumentKind, typeof FileText> = {
  general: FileText,
  course: BookOpen,
  exercice_sheet: PencilSimple,
  exam: ListChecks,
};

export default function AdminContent() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<AdminContentDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [teachers, setTeachers] = useState<AdminContentTeacherOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<AdminContentDocumentKind | null>(null);
  const [schoolFilter, setSchoolFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');

  useEffect(() => {
    fetchAdminSchools()
      .then(setSchools)
      .catch(() => setSchools([]));
  }, []);

  /* Le filtre enseignant n'a de sens qu'une fois une école choisie : sans elle,
     il n'existe pas de liste "tous les enseignants de la plateforme" à charger. */
  useEffect(() => {
    setTeacherFilter('');
    if (!schoolFilter) { setTeachers([]); return; }
    listAdminContentTeachers(schoolFilter)
      .then(setTeachers)
      .catch(() => setTeachers([]));
  }, [schoolFilter]);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    listAdminContentDocuments({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      kind: kindFilter ?? undefined,
      schoolId: schoolFilter || undefined,
      teacherId: teacherFilter || undefined,
    })
      .then((res) => { setDocuments(res.items); setTotal(res.total); })
      .catch(() => { setDocuments([]); setTotal(0); setFailed(true); })
      .finally(() => setLoading(false));
  }, [page, search, kindFilter, schoolFilter, teacherFilter]);

  useEffect(() => { load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const handleKind = (k: AdminContentDocumentKind | null) => { setKindFilter(k); setPage(1); };
  const handleSchool = (id: string) => { setSchoolFilter(id); setPage(1); };
  const handleTeacher = (id: string) => { setTeacherFilter(id); setPage(1); };

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/admin" backLabel="Admin" icon={HEADER_ICON}>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className={HEADER_TITLE_CLASS}>Contenus</h1>
            <span className={HEADER_BADGE_CLASS}>Admin</span>
          </div>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Cours, exercices et évaluations créés par les enseignants de toutes les écoles.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[180px] max-w-[300px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]" size={14} />
            <input
              type="search"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Rechercher un titre…"
              aria-label="Rechercher un document"
              className="w-full pl-8 pr-3 py-[7px] rounded-[8px] border border-[var(--naia-line)] bg-[var(--naia-card)] text-[13px] text-[var(--naia-text-primary)] outline-none focus:border-primary transition-colors font-[inherit]"
            />
          </div>

          <NativeSelect
            value={schoolFilter}
            onChange={(e) => handleSchool(e.target.value)}
            aria-label="Filtrer par école"
            className="h-9 w-auto min-w-[160px] text-[13px]"
          >
            <option value="">Toutes les écoles</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </NativeSelect>

          <NativeSelect
            value={teacherFilter}
            onChange={(e) => handleTeacher(e.target.value)}
            aria-label="Filtrer par enseignant"
            disabled={!schoolFilter}
            title={schoolFilter ? undefined : 'Choisissez une école pour filtrer par enseignant'}
            className="h-9 w-auto min-w-[160px] text-[13px]"
          >
            <option value="">{schoolFilter ? 'Tous les enseignants' : 'Choisissez une école'}</option>
            {teachers.map((t) => (
              <option key={t.user_id} value={t.user_id}>{`${t.first_name} ${t.last_name}`.trim() || t.email || 'Enseignant'}</option>
            ))}
          </NativeSelect>

          {(Object.keys(KIND_LABEL) as AdminContentDocumentKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => handleKind(kindFilter === k ? null : k)}
              className={cn(
                'px-[11px] py-[5px] rounded-full border text-[12px] font-bold cursor-pointer transition-colors font-[inherit]',
                kindFilter === k
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-[var(--naia-line)] bg-[var(--naia-card)] text-[var(--naia-text-secondary)] hover:border-primary',
              )}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : failed ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="flex items-start gap-2 m-0 text-[13.5px] text-[var(--naia-text-primary)]">
              <WarningCircle size={18} weight="fill" className="text-destructive shrink-0 mt-px" />
              <span>Les contenus n'ont pas pu être chargés.</span>
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={load}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-muted-foreground">
            <Files size={44} weight="light" className="mb-3 opacity-40" />
            <p className="font-semibold text-[14px] text-foreground">Aucun document ne correspond</p>
            <p className="text-[13px] mt-1">Essayez un autre filtre ou un autre mot-clé.</p>
          </div>
        ) : (
          <>
            <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]">
              {documents.map((doc) => {
                const Icon = KIND_ICON[doc.kind];
                return (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => navigate(`/admin/contenus/${doc.id}`)}
                    className="flex items-center gap-3 w-full px-4 py-[13px] bg-transparent border-none cursor-pointer text-left font-[inherit] hover:bg-accent transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-[8px] bg-primary/10 flex items-center justify-center">
                      <Icon size={17} weight="fill" className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={LIST_ROW_TITLE_CLASS} title={displayTitle(doc)}>{displayTitle(doc)}</span>
                        <span className="text-[10.5px] font-extrabold text-[var(--naia-text-secondary)] uppercase tracking-[0.5px]">
                          {KIND_LABEL[doc.kind]}
                        </span>
                      </div>
                      <div className={LIST_ROW_META_CLASS}>
                        <span>{doc.teacher_name}</span>
                        <span aria-hidden>·</span>
                        <span>{doc.school_name}</span>
                        <span aria-hidden>·</span>
                        <span>
                          modifié le <span className="sm:hidden">{fmtListDateShort(doc.updated_at)}</span>
                          <span className="hidden sm:inline">{fmtListDate(doc.updated_at)}</span>
                        </span>
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[var(--naia-text-secondary)] shrink-0" />
                  </button>
                );
              })}
            </div>

            {pageCount > 1 && (
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="gap-1.5"
                >
                  <ArrowLeft size={13} /> Précédent
                </Button>
                <span className="text-[12.5px] text-[var(--naia-text-secondary)] font-semibold tabular-nums">
                  Page {page} / {pageCount} · {total} document{total !== 1 ? 's' : ''}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                  className="gap-1.5"
                >
                  Suivant <ArrowRight size={13} />
                </Button>
              </div>
            )}

            {pageCount === 1 && (
              <p className="text-[12px] text-[var(--naia-text-secondary)] mt-3 text-right tabular-nums">
                {total} document{total !== 1 ? 's' : ''}
              </p>
            )}
          </>
        )}
      </main>
    </>
  );
}
